<template>
  <div class="service-product-container">
    <BreadCrumbs :step=1></BreadCrumbs>
    <div class="header-container" :class="brand.name">
      <div class="heading" :class="brand.name">Exclusive Online-Purchase Only Specials</div>
      <div class="subheading" :class="brand.name">Save when you purchase online.</div>
      <div v-if="isLoading" class="loading-spinner">
        Loading Online Only Specials...
        <div class="spinner-border">
        </div>
      </div>
    </div>
    <div v-if=noSpecialsAvailable class="alert">
      <p>Currently, there are no exclusive online-purchase specials available.</p>
      <p>Please contact <b>{{ dealer.name }}</b> to learn more about available specials for your vehicle at
      <a v-bind:href="`tel:${bdcPhoneCompact}`" v-on:click="trackBdcPhone" class="alert">{{ dealer.phone.bdc | phoneFormatter }}</a>.</p>
    </div>
    <div v-else>
      <div v-if="categories.length > 1" class="category-container">
        <div class="category-list" v-for="category, index in categories" :key="category">
          <a href="javascript:void(0)"
          target="_self"
          :class="buttonClass(category)"
          v-on:click="categoryFilter($event)"
          :id="category">{{ category }}
          </a>
          <div v-if="index < 2" class="category-div"></div>
        </div>
      </div>
      <div v-if="isFiltering" class="spinner-border">
      </div>
      <div v-if="filteredSpecialsError" class="error-message">
        <p>Currently, there are no exclusive online-purchase specials for {{ selectedCategory }} available.</p>
        <p>Please contact <b>{{ dealer.name }}</b> to learn more about available specials for your vehicle at
        <a v-bind:href="`tel:${bdcPhoneCompact}`" v-on:click="trackBdcPhone" class="alert">{{ dealer.phone.bdc | phoneFormatter }}</a>.</p>
      </div>
      <div v-else>
        <div class="bundle-container">
          <BundleItem class="product-list" v-for="option in sortedBundles" :product="option" :brand="brand" :key="option.bundle_id" :in-iframe="inIframe">
          </BundleItem>
        </div>
        <div class="product-container">
          <ProductItem class="product-list" v-for="option in sortedProducts" :product="option" :brand="brand" :key="option.product_id" :in-iframe="inIframe">
          </ProductItem>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ProductItem from '@/components/ecomm/product/ProductItem'
import BundleItem from '@/components/ecomm/product/BundleItem'
import BreadCrumbs from '@/components/ecomm/BreadCrumbs'
import { useBreadcrumbService } from '@/services/breadcrumb-service'
import Tracking from '@/mixins/tracking-mixin'

export default {
  name: 'ServiceList',
  mixins: [Tracking],
  data () {
    const { setSteps, getCurrentStep, resetBreadcrumb } = useBreadcrumbService()
    return {
      logEshopListEvent: true,
      categories: [],
      selectedCategory: '',
      filteredProducts: [],
      filteredBundles: [],
      setSteps,
      getCurrentStep,
      resetBreadcrumb,
      isFiltering: false
    }
  },
  props: {
    inIframe: Boolean
  },
  components: {
    ProductItem,
    BundleItem,
    BreadCrumbs
  },
  watch: {
    // the same component is reused between params
    // this watch ensures we reset the page when the params change
    '$route.params': 'reloadPage'
  },
  created () {
    this.fetchData()
  },
  updated () {
    this.setColumnCount()
  },
  computed: {
    ...mapState({
      brand: state => state.brand,
      dealer: state => state.dealer,
      session: state => state.session
    }),
    ...mapGetters({
      listProductsByCategory: ['productVariants/listProductsByCategory'],
      listBundles: ['productVariants/listServiceBundles'],
      serviceEnabled: 'ecommServiceEnabled'
    }),
    noSpecialsAvailable () {
      return !this.isLoading && (!this.serviceEnabled || (this.filteredProducts.length === 0 && this.filteredBundles.length === 0))
    },
    filteredSpecialsError () {
      let filteredSpecialsError = false
      if (this.selectedCategory) {
        if (this.selectedCategory !== 'Shop All' && !this.categories.includes(this.selectedCategory)) {
          filteredSpecialsError = true
        } else if (this.filteredBundles.length === 0 && this.filteredProducts.length === 0) {
          filteredSpecialsError = true
        }
      }
      return filteredSpecialsError
    },
    sortedBundles () {
      // Sort using natural order sorting so a mix of numbers and characters sort as we expect ex: 10K, 20K, 100K etc
      const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' })
      let sortedBundle = this.filteredBundles
      sortedBundle = sortedBundle.sort((a, b) => { return collator.compare(a.title, b.title) })
      return sortedBundle.length > 0 ? sortedBundle : []
    },
    sortedProducts () {
      // Sort using natural order sorting so a mix of numbers and characters sort as we expect ex: 10K, 20K, 100K etc
      const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' })
      let sortedProducts = this.filteredProducts
      sortedProducts = sortedProducts.sort((a, b) => { return collator.compare(a.title, b.title) })
      return sortedProducts.length > 0 ? sortedProducts : []
    }
  },
  methods: {
    buttonClass (selectButton) {
      if (this.selectedCategory === selectButton) {
        return `${this.brand.name} category-button selected`
      } else {
        return `${this.brand.name} category-button`
      }
    },
    categoryFilter (event) {
      this.isFiltering = true
      // Clear styling for all past selections
      const selectedClass = document.getElementsByClassName('category-button')
      for (let i = 0; i < selectedClass.length; i++) {
        const clearSelected = selectedClass[i].getElementsByClassName('selected')
        if (clearSelected) {
          selectedClass[i].classList.remove('selected')
        }
      }
      // Style selected option
      const selected = document.getElementById(event.target.id)
      if (selected) {
        selected.classList.add('selected')
        this.selectedCategory = event.target.id
        if (this.selectedCategory === 'Shop All') {
          this.listFilteredProducts()
          this.listFilteredBundles()
          this.$store.commit('session/setServiceFilter', { serviceFilter: '' })
        } else {
          this.listFilteredProducts(event.target.id)
          this.listFilteredBundles(event.target.id)
          this.$store.commit('session/setServiceFilter', { serviceFilter: this.selectedCategory })
        }
      }
      setTimeout(() => {
        this.isFiltering = false
      }, 500)
    },
    async fetchData () {
      this.isLoading = true
      await this.$store.dispatch('productVariants/getProducts')
      this.setCategories()
      if (this.$route.params && this.$route.params.category) {
        this.selectedCategory = this.$route.params.category.toUpperCase().substring(0, 1) + this.$route.params.category.toLowerCase().substring(1)
        this.$store.commit('session/setServiceFilter', { serviceFilter: this.$route.params.category.toLowerCase() })
        this.listFilteredProducts(this.selectedCategory)
        this.listFilteredBundles(this.selectedCategory)
      } else {
        this.selectedCategory = 'Shop All'
        this.listFilteredProducts()
        this.listFilteredBundles()
      }
      if (this.$route.params && this.$route.params.step && this.$route.params.step === 1) {
        this.resetBreadcrumb()
      }
      this.eshopEvent()
      this.setColumnCount()
      this.isLoading = false
    },
    eshopEvent () { // only send the list of offers once for tracking
      if (this.logEshopListEvent) {
        if (this.listBundles && this.listBundles.length > 0) {
          this.logEshopListEvent = this.logEvent('eshop', this.listBundles)
        }
        if (this.filteredProducts && this.filteredProducts.length > 0) {
          this.logEshopListEvent = this.logEvent('eshop', this.filteredProducts)
        }
      }
    },
    listFilteredProducts (filter) {
      let filteredProducts = []

      if (filter) {
        filteredProducts = this.listProductsByCategory(filter.toLowerCase())
      } else {
        this.categories.forEach(element => {
          filteredProducts = filteredProducts.concat(this.listProductsByCategory(element.toLowerCase()))
        })
      }
      this.filteredProducts = filteredProducts
    },
    listFilteredBundles (filter) {
      let filteredBundles = []
      if (filter) {
        filteredBundles = this.listBundles.filter(bundle => bundle.category.toLowerCase() === filter.toLowerCase())
      } else {
        this.categories.forEach(element => {
          filteredBundles = filteredBundles.concat(this.listBundles.filter(bundle => bundle.category.toLowerCase() === element.toLowerCase()))
        })
      }
      this.filteredBundles = filteredBundles
    },
    logEvent (interactionType, itemsArray) {
      this.$store.dispatch('session/logInteraction', { interaction_type: interactionType, action: 'view_item_list', metadata: this.$store.getters['customer/getCustomerInteractionData'] })
      this.$store.dispatch('track/gaEcommViewEvent', { vm: this, event: 'view_item_list', brand: this.brand.name, items: itemsArray })
      this.$store.dispatch('track/facebookEvent', { event: 'View-Item-List' })
      return false
    },
    setCategories () {
      this.categories = []
      if (this.dealer.dshFeatures.includes('accessories')) {
        this.categories.push('Accessories')
      }
      if (this.dealer.dshFeatures.includes('service')) {
        this.categories.push('Service')
      }
      if (this.categories.length > 1) {
        this.categories.push('Shop All')
      }
    },
    setColumnCount () {
      const totalCount = this.filteredProducts.length
      if (totalCount <= 1) {
        document.documentElement.style.setProperty('--num-column-mid', 1)
        document.documentElement.style.setProperty('--num-column-large', 1)
      } else if (totalCount <= 2) {
        document.documentElement.style.setProperty('--num-column-mid', 2)
        document.documentElement.style.setProperty('--num-column-large', 2)
      } else {
        document.documentElement.style.setProperty('--num-column-mid', 2)
        document.documentElement.style.setProperty('--num-column-large', 3)
      }
      return totalCount
    },
    reloadPage (newvalue, oldvalue) {
      if (newvalue !== oldvalue) {
        this.isFiltering = true
        this.fetchData()
        setTimeout(() => {
          this.isFiltering = false
        }, 500)
      }
    }
  }
}

</script>

<style scoped>
.product-list {
  animation: fadeInLoad 2s;
}
@keyframes fadeInLoad {
  from {
    opacity:0;
  }
  to {
    opacity:1;
  }
}
.header-container {
  grid-column: 2 / span 1;
  text-align: center;
  margin: 20px 0px;
}
.category-container {
  display: inline-flex;
  flex-direction: row;
  font-size: 1.5rem;
  /* gap: 20px 20px; */
}
.bundle-container {
  display: grid;
  gap: 20px 20px;
  margin: 20px;
}
.product-container {
  margin: 20px;
  grid-column: 2 / span 1;
  justify-self: center;
  display: grid;
  gap: 20px 20px;
  grid-template-columns: auto;
  justify-content: center;
}
.heading {
  font-size: 1.5em;
  line-height: 1.2;
  font-family: var(--title-font-stack);
  margin-bottom: 10px;
}
.alert {
  color: red;
}
.error-message {
  padding: 20px;
  color: red;
}
.loading-spinner {
  width: 100%;
  padding-top: 20px;
  padding-bottom: 40px;
  justify-content: center;
  text-align: center;
}
.category-list {
  display: flex;
  flex-direction: row;
  color: grey;
  /* background-color:var(--variant-alt-color); */
}
.category-div {
  border-left: solid 1px grey;
}
.category-list:hover {
  cursor: pointer;
  color: var(--variant-color);
}
.category-button {
  padding: 5px 15px;
  color: grey;
  /* border-left: solid 1px grey;
  border-right: solid 1px grey; */
}
.selected {
  color: var(--variant-color)!important;
  /* background-color: var(--variant-color)!important; */
}
/* styles for different screen widths follow */
@media only screen and (min-width: 576px) {
  .product-container {
    grid-template-columns: repeat(var(--num-column-mid), minmax(250px, 384px));
  }
  .heading {
    font-size: 2em;
  }
  .subheading {
    font-size: 1.2em;
  }
  .category-container {
    flex-direction: row;
  }
}
@media only screen and (min-width: 768px) {
}
@media only screen and (min-width: 992px) {
  .product-container {
    grid-template-columns: repeat(var(--num-column-large), minmax(300px, 384px));
  }
}
@media only screen and (min-width: 1200px) {
}
</style>
